import type { Department, PositionTitle } from '@/types/oscar';

export interface DepartmentFilter {
  /** Geographic code (B / N / R); '' = all. */
  geo: string;
  /** Business unit code; '' = all. */
  bu: string;
  /** Free text over code, description and the department's position titles. */
  q: string;
}

export interface DepartmentRow {
  department: Department;
  /** The department's live position titles, A to Z. */
  titles: PositionTitle[];
  /** Ids of titles that matched the search text, so the row can open on them. */
  titleHits: Set<number>;
}

/**
 * The department list the builder picks from, filtered like the Position Title
 * Allocator: geography, business unit, then text. Text matches the code,
 * description, business unit or geography, or any title in the department, so
 * searching "Driver" lists the departments that hold a driver title.
 */
export function filterDepartments(departments: Department[], titles: PositionTitle[], f: DepartmentFilter): DepartmentRow[] {
  const q = f.q.trim().toLowerCase();
  const byDept = new Map<string, PositionTitle[]>();
  for (const t of titles) {
    if (t.IsDeleted || !t.DepartmentCode) continue;
    const list = byDept.get(t.DepartmentCode) ?? [];
    list.push(t);
    byDept.set(t.DepartmentCode, list);
  }

  const rows: DepartmentRow[] = [];
  for (const d of departments) {
    if (d.IsDeleted) continue;
    if (f.geo && d.GeographicCode !== f.geo) continue;
    if (f.bu && d.BusinessUnitCode !== f.bu) continue;
    const own = [...(byDept.get(d.DepartmentCode) ?? [])].sort((a, b) => a.PositionTitle.localeCompare(b.PositionTitle));
    const titleHits = new Set(q ? own.filter((t) => t.PositionTitle.toLowerCase().includes(q)).map((t) => t.PositionTitleId) : []);
    const deptHit = !q || [d.DepartmentCode, d.DepartmentDescription, d.BusinessUnitDescription, d.GeographicDescription, d.BUSubDescription]
      .some((s) => s?.toLowerCase().includes(q));
    if (!deptHit && titleHits.size === 0) continue;
    rows.push({ department: d, titles: own, titleHits });
  }
  return rows.sort((a, b) => a.department.DepartmentCode.localeCompare(b.department.DepartmentCode));
}

/** Business units present in the list, as [code, description], A to Z by description. */
export function businessUnits(departments: Department[]): [string, string][] {
  const map = new Map<string, string>();
  for (const d of departments) if (!d.IsDeleted) map.set(d.BusinessUnitCode, d.BusinessUnitDescription);
  return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
}

/** Add `values` to `list`, keeping order and skipping case-insensitive duplicates. */
export function addUnique(list: string[], values: string[]): string[] {
  const seen = new Set(list.map((v) => v.toLowerCase()));
  const out = [...list];
  for (const v of values) {
    if (seen.has(v.toLowerCase())) continue;
    seen.add(v.toLowerCase());
    out.push(v);
  }
  return out;
}
